import React from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { Button, Flex, Select, Switch, Text, TextField, type SelectOption } from '@chatsdet/ui';
import { CheckCircle, PlugsConnected, Plus, Trash, XCircle } from '@phosphor-icons/react';
import { API_URL } from '../../../config';
import { toApiError, ApiError } from '../../../lib/api-error';
import { MacroRichTextField } from './MacroRichTextField';
import { ConfigSection } from './ConfigSection';
import { ActionOutputsEditor } from './ActionOutputsEditor';
import { CredentialPersonaPicker, useCredentialAndPersonaOptions } from './CredentialPersonaPicker';
import { usePropertySummaries } from '../../../hooks/usePropertyMetadata';

type McpNodeConfigPanelProps = {
  localData: Record<string, any>;
  setLocalData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
};

/**
 * Config panel for mcp nodes.
 *
 * Targets an MCP application (an org property with `type: 'mcp'`) and carries
 * plain-language instructions the agent executes against the server's tools.
 * Auth resolves through a persona (`credentialTemplateId`, preferred) or a
 * direct credential pin (`credentialId`, escape hatch) scoped to the selected
 * application. Declared outputs register as `$var.*` variables downstream.
 */
export const McpNodeConfigPanel: React.FC<McpNodeConfigPanelProps> = ({
  localData,
  setLocalData,
}) => {
  const propertiesQ = usePropertySummaries();
  const propertyId = (localData.propertyId as string | undefined) ?? '';
  const credentialTemplateId = (localData.credentialTemplateId as string | undefined) ?? null;
  const credentialId = (localData.credentialId as string | undefined) ?? null;
  const instructions = (localData.instructions as string | undefined) ?? '';
  const declaredOutputs: string[] = Array.isArray(localData.declaredOutputs)
    ? (localData.declaredOutputs as string[])
    : [];
  const toolAllowlist: string[] = Array.isArray(localData.toolAllowlist)
    ? (localData.toolAllowlist as string[])
    : [];

  const mcpProperties = React.useMemo(
    () => (propertiesQ.data ?? []).filter((p) => p.type === 'mcp'),
    [propertiesQ.data]
  );

  const propertyOptions: SelectOption[] = React.useMemo(
    () =>
      mcpProperties.map((p) => ({
        id: p.id,
        label: p.name,
        description: p.description ?? undefined,
      })),
    [mcpProperties]
  );

  // Personas/credentials scoped to the selected MCP application.
  const credentialOptions = useCredentialAndPersonaOptions(propertyId || null);

  const handleSelectProperty = (id: string) => {
    setLocalData((prev) => {
      if (prev.propertyId === id) return prev;
      // A different application's personas don't apply — clear the selection.
      return {
        ...prev,
        propertyId: id,
        credentialTemplateId: undefined,
        credentialId: undefined,
      };
    });
  };

  const handlePickCredential = (id: string | null) => {
    setLocalData((prev) => {
      if (id === null) {
        return { ...prev, credentialTemplateId: undefined, credentialId: undefined };
      }
      // Personas (credential templates) are preferred; a concrete credential id
      // is the direct-pin escape hatch.
      if (credentialOptions.templateIdSet.has(id)) {
        return { ...prev, credentialTemplateId: id, credentialId: undefined };
      }
      return { ...prev, credentialId: id, credentialTemplateId: undefined };
    });
  };

  const handleAddOutput = (name: string) =>
    setLocalData((prev) => ({
      ...prev,
      declaredOutputs: [...((prev.declaredOutputs ?? []) as string[]), name],
    }));

  const handleDeleteOutput = (name: string) =>
    setLocalData((prev) => ({
      ...prev,
      declaredOutputs: ((prev.declaredOutputs ?? []) as string[]).filter((n) => n !== name),
    }));

  const clearOutputs = () => setLocalData((prev) => ({ ...prev, declaredOutputs: [] }));

  const handleAddTool = (name: string) =>
    setLocalData((prev) => {
      const existing = Array.isArray(prev.toolAllowlist) ? (prev.toolAllowlist as string[]) : [];
      if (existing.includes(name)) return prev;
      return { ...prev, toolAllowlist: [...existing, name] };
    });

  const handleDeleteTool = (name: string) =>
    setLocalData((prev) => {
      const next = ((prev.toolAllowlist ?? []) as string[]).filter((n) => n !== name);
      return { ...prev, toolAllowlist: next.length > 0 ? next : undefined };
    });

  return (
    <div className="space-y-4">
      <Text size="sm" className="text-muted-foreground">
        Drive an MCP server with plain-language instructions — no browser. Declared outputs register
        as variables for downstream steps.
      </Text>

      {propertyOptions.length === 0 ? (
        <Text size="sm" className="text-muted-foreground">
          {propertiesQ.isLoading ? (
            'Loading applications…'
          ) : (
            <>
              No MCP applications yet. Create one under{' '}
              <Link to="/applications" className="text-primary underline underline-offset-2">
                Applications
              </Link>
              .
            </>
          )}
        </Text>
      ) : (
        <Select
          label="Application"
          items={propertyOptions}
          selectedKey={propertyId || null}
          placeholder="Choose an MCP application…"
          onSelectionChange={(key) => handleSelectProperty(String(key))}
        />
      )}

      {/* Stale reference (e.g. the application was deleted or re-typed) */}
      {propertyId &&
        propertyOptions.length > 0 &&
        !mcpProperties.some((p) => p.id === propertyId) && (
          <Text size="sm" className="text-amber-600">
            The selected application is no longer an MCP application. Choose another one.
          </Text>
        )}

      {propertyId && (
        <CredentialPersonaPicker
          templates={credentialOptions.templates}
          credentials={credentialOptions.credentials}
          propertyNameById={credentialOptions.propertyNameById}
          templateNameById={credentialOptions.templateNameById}
          isLoading={credentialOptions.isLoading}
          selectedTemplateId={credentialTemplateId}
          selectedCredentialId={credentialId}
          onPick={handlePickCredential}
          label="Persona"
        />
      )}

      {propertyId && (credentialTemplateId || credentialId) && (
        <McpTestConnectionSection
          key={`${propertyId}:${credentialTemplateId ?? credentialId}`}
          propertyId={propertyId}
          credentialTemplateId={credentialTemplateId}
          credentialId={credentialId}
        />
      )}

      <MacroRichTextField
        multiline
        label="Instructions"
        placeholder="Describe what the agent should accomplish via the server's tools, including specifics and exact values… (type $ for macros)"
        value={instructions}
        onChange={(value) => setLocalData((prev) => ({ ...prev, instructions: value }))}
        description="Used by the agent to drive the MCP server's tools."
        rows={6}
      />

      <ConfigSection title="Outputs" count={declaredOutputs.length}>
        <ActionOutputsEditor
          declaredOutputs={declaredOutputs}
          capturedVariableKeys={[]}
          onAddOutput={handleAddOutput}
          onDeleteOutput={handleDeleteOutput}
          onClearAll={clearOutputs}
        />
      </ConfigSection>

      <ConfigSection title="Tool allowlist" count={toolAllowlist.length}>
        <ToolAllowlistEditor
          tools={toolAllowlist}
          onAdd={handleAddTool}
          onDelete={handleDeleteTool}
        />
      </ConfigSection>

      <div className="border-t border-border/40 py-3 space-y-1">
        <Switch
          size="sm"
          isSelected={localData.alwaysUseAgent ?? false}
          onChange={(isSelected) =>
            setLocalData((prev) => ({ ...prev, alwaysUseAgent: isSelected }))
          }
          label={
            <Text size="xs" className="text-muted-foreground">
              Always use agent
            </Text>
          }
        />
        <Text size="xs" className="text-muted-foreground">
          Skip cache replay and run the agent against the server every time.
        </Text>
      </div>
    </div>
  );
};

type McpTestConnectionResult = {
  environmentName: string;
  tools: Array<{ name: string; description?: string }>;
};

/**
 * Compact connection probe: POSTs the node's application + persona/credential
 * selection to the test-connection endpoint (default environment) and lists
 * the advertised tools on success, or the typed error on failure.
 */
const McpTestConnectionSection: React.FC<{
  propertyId: string;
  credentialTemplateId: string | null;
  credentialId: string | null;
}> = ({ propertyId, credentialTemplateId, credentialId }) => {
  const testM = useMutation({
    mutationFn: async (): Promise<McpTestConnectionResult> => {
      const r = await fetch(
        `${API_URL}/org/properties/${encodeURIComponent(propertyId)}/mcp/test-connection`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            ...(credentialTemplateId ? { credentialTemplateId } : {}),
            ...(credentialId ? { credentialId } : {}),
          }),
        }
      );
      if (!r.ok) throw await toApiError(r, 'Connection test failed');
      return (await r.json()) as McpTestConnectionResult;
    },
  });

  const errorDetails =
    testM.error instanceof ApiError && typeof testM.error.details?.details === 'string'
      ? testM.error.details.details
      : null;

  return (
    <div className="rounded-md border border-border/60 p-3 space-y-2">
      <Flex align="center" justify="between">
        <Flex align="center" gap="sm">
          <PlugsConnected size={14} className="text-muted-foreground" />
          <Text size="sm" weight="medium">
            Connection
          </Text>
        </Flex>
        <Button
          variant="outline"
          size="sm"
          isDisabled={testM.isPending}
          onPress={() => testM.mutate()}
        >
          {testM.isPending ? 'Testing…' : 'Test connection'}
        </Button>
      </Flex>

      {testM.isSuccess && (
        <div className="space-y-1">
          <Flex align="center" gap="sm">
            <CheckCircle size={14} weight="bold" className="text-success" />
            <Text size="xs" className="text-muted-foreground">
              Connected to “{testM.data.environmentName}” — {testM.data.tools.length} tool
              {testM.data.tools.length === 1 ? '' : 's'}
            </Text>
          </Flex>
          <div className="max-h-40 overflow-y-auto space-y-0.5">
            {testM.data.tools.map((tool) => (
              <div key={tool.name} className="px-2 py-0.5 rounded bg-muted/30">
                <Text size="xs" className="font-mono">
                  {tool.name}
                </Text>
                {tool.description && (
                  <Text size="xs" className="text-muted-foreground truncate">
                    {tool.description}
                  </Text>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {testM.isError && (
        <Flex align="start" gap="sm">
          <XCircle size={14} weight="bold" className="text-destructive shrink-0 mt-0.5" />
          <Text size="xs" className="text-destructive">
            {testM.error instanceof Error ? testM.error.message : 'Connection test failed'}
            {errorDetails ? ` — ${errorDetails}` : ''}
          </Text>
        </Flex>
      )}
    </div>
  );
};

/** Chip-style editor for the optional tool allowlist (verbatim tool names). */
const ToolAllowlistEditor: React.FC<{
  tools: string[];
  onAdd: (name: string) => void;
  onDelete: (name: string) => void;
}> = ({ tools, onAdd, onDelete }) => {
  const [isAdding, setIsAdding] = React.useState(false);
  const [newName, setNewName] = React.useState('');

  const resetAddForm = () => {
    setIsAdding(false);
    setNewName('');
  };

  const handleSubmit = () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    onAdd(trimmed);
    resetAddForm();
  };

  return (
    <div>
      <Text size="sm" className="text-muted-foreground mt-1">
        Narrow the tools the agent sees, using the names the server advertises. Fewer tools means a
        smaller prompt and a more focused agent. Leave empty to expose everything.
      </Text>

      {tools.length > 0 && (
        <div className="mt-3 space-y-2">
          {tools.map((name) => (
            <Flex
              key={name}
              align="center"
              justify="between"
              className="rounded-md px-3 py-1.5 bg-muted/30"
            >
              <Text size="sm" className="font-mono">
                {name}
              </Text>
              <Button
                variant="icon"
                size="sm"
                iconOnly
                tooltip="Remove tool"
                onPress={() => onDelete(name)}
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash size={14} />
              </Button>
            </Flex>
          ))}
        </div>
      )}

      {isAdding ? (
        <div className="mt-3 rounded-md border border-border/60 p-3 space-y-3">
          <TextField
            label="Tool name"
            placeholder="e.g. search_issues"
            value={newName}
            onChange={setNewName}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit();
              } else if (e.key === 'Escape') {
                resetAddForm();
              }
            }}
          />
          <Flex gap="sm" justify="end">
            <Button variant="ghost" size="sm" onPress={resetAddForm}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" isDisabled={!newName.trim()} onPress={handleSubmit}>
              Add
            </Button>
          </Flex>
        </div>
      ) : (
        <div className="mt-3">
          <Button
            variant="outline"
            size="sm"
            iconLeft={<Plus size={14} />}
            onPress={() => setIsAdding(true)}
          >
            Add tool
          </Button>
        </div>
      )}
    </div>
  );
};
